import { SiteHeader } from "@/components/site-header"

export default function RolesPermissionsGuidePage() {
  return (
    <>
      <SiteHeader title="Roles y Permisos — Guía técnica" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="w-full max-w-3xl space-y-10">

          <p className="text-sm text-muted-foreground">
            El sistema de permisos es centralizado: un único archivo controla qué puede ver y hacer cada rol en todo
            el sistema — tanto la UI como las rutas de navegación. No hay lógica de permisos esparcida en los componentes.
          </p>

          {/* ─── Arquitectura ──────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Arquitectura</h2>
            <p className="text-sm text-muted-foreground">Tres capas coordinadas desde un único archivo de configuración:</p>
            <div className="border rounded-lg overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Capa</th>
                    <th className="text-left px-4 py-2 font-medium">Qué controla</th>
                    <th className="text-left px-4 py-2 font-medium">Dónde</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">PERMISSIONS map</td>
                    <td className="px-4 py-2 text-muted-foreground">Fuente de verdad de todos los permisos</td>
                    <td className="px-4 py-2 font-mono text-xs">packages/shared/src/config/permissions.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">usePermissions()</td>
                    <td className="px-4 py-2 text-muted-foreground">Muestra/oculta botones y secciones en la UI</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/web/src/hooks/use-permissions.ts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">RequireRole</td>
                    <td className="px-4 py-2 text-muted-foreground">Bloquea el acceso a rutas completas</td>
                    <td className="px-4 py-2 font-mono text-xs">apps/web/src/App.tsx</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── El mapa de permisos ───────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">El mapa de permisos</h2>
            <p className="text-sm text-muted-foreground">
              Cada rol tiene un objeto con los 16 recursos del sistema. Cada recurso tiene 4 acciones posibles.
            </p>
            <div className="bg-muted rounded-md p-4 text-xs font-mono space-y-1 overflow-x-auto">
              <p className="text-muted-foreground">{"// packages/shared/src/config/permissions.ts"}</p>
              <p className="mt-2">{"export const PERMISSIONS = {"}</p>
              <p className="pl-4">{"admin: {"}</p>
              <p className="pl-8">{"geo:     ALL,   // { visit, write, delete, restore }"}</p>
              <p className="pl-8">{"trips:   ALL,"}</p>
              <p className="pl-8">{"audit:   NONE,  // admin no ve auditoría por defecto"}</p>
              <p className="pl-8">{"roles:   ALL,   // solo admin puede ver Roles y permisos"}</p>
              <p className="pl-8">{"// ..."}</p>
              <p className="pl-4">{"},"}</p>
              <p className="pl-4">{"operator: {"}</p>
              <p className="pl-8">{"geo:     READ,  // solo lectura"}</p>
              <p className="pl-8">{"audit:   NONE,"}</p>
              <p className="pl-8">{"// ..."}</p>
              <p className="pl-4">{"},"}</p>
              <p>{"}"}</p>
            </div>
            <div className="border rounded-lg overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Constante</th>
                    <th className="text-left px-4 py-2 font-medium">visit</th>
                    <th className="text-left px-4 py-2 font-medium">write</th>
                    <th className="text-left px-4 py-2 font-medium">delete</th>
                    <th className="text-left px-4 py-2 font-medium">restore</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-xs font-mono">
                  <tr>
                    <td className="px-4 py-2">ALL</td>
                    <td className="px-4 py-2 text-green-600 dark:text-green-400">✓</td>
                    <td className="px-4 py-2 text-green-600 dark:text-green-400">✓</td>
                    <td className="px-4 py-2 text-green-600 dark:text-green-400">✓</td>
                    <td className="px-4 py-2 text-green-600 dark:text-green-400">✓</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">READ / VIEW_ONLY</td>
                    <td className="px-4 py-2 text-green-600 dark:text-green-400">✓</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">NONE</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                    <td className="px-4 py-2 text-muted-foreground">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Recursos disponibles ──────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Recursos disponibles</h2>
            <div className="border rounded-lg overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Resource</th>
                    <th className="text-left px-4 py-2 font-medium">Secciones del sidebar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-xs">
                  {[
                    ["geo",       "Países, Provincias, Ciudades, Rutas"],
                    ["trips",     "Viajes, Tipos de servicio"],
                    ["shipments", "Cargas → Todas"],
                    ["cargo",     "Cargas → Nacional / Internacional / Batea / Productos / Tipos"],
                    ["smartgt",   "SmartGT → CRTs, Chofer"],
                    ["units",     "Flota → Tipos de unidad, Unidades"],
                    ["mobiles",   "Flota → Móviles"],
                    ["drivers",   "Relaciones → Choferes, Remitos"],
                    ["clients",   "Relaciones → Clientes"],
                    ["parties",   "Relaciones → Partes"],
                    ["users",     "Sistema → Usuarios"],
                    ["roles",     "Sistema → Roles y permisos"],
                    ["audit",     "Sistema → Auditoría, SmartGT → Log de importación CRT"],
                    ["config",    "Sistema → Tipos de documento, Monedas, Conceptos de pago"],
                    ["reports",   "Ayuda → Reportes"],
                    ["docs",      "Ayuda → Documentación"],
                  ].map(([r, s]) => (
                    <tr key={r}>
                      <td className="px-4 py-2 font-mono">{r}</td>
                      <td className="px-4 py-2 text-muted-foreground">{s}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Cómo cambiar un permiso ───────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Cómo cambiar un permiso</h2>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>
                Abrí{" "}
                <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                  packages/shared/src/config/permissions.ts
                </code>
              </li>
              <li>
                Cambiá el valor del recurso para el rol deseado:{" "}
                <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">ALL</code>,{" "}
                <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">READ</code>,{" "}
                <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">NONE</code>, o un objeto personalizado.
              </li>
              <li>El cambio afecta automáticamente: visibilidad de botones, sidebar y guards de rutas.</li>
              <li>Redesplegar para que los usuarios existentes reciban el cambio (el JWT se re-valida en cada request).</li>
            </ol>
            <div className="bg-muted rounded-md p-4 text-xs font-mono space-y-1">
              <p className="text-muted-foreground">{"// Ejemplo: darle acceso de lectura a auditoría para admin"}</p>
              <p>{"admin: {"}</p>
              <p className="pl-4 text-destructive line-through">{"audit: NONE,"}</p>
              <p className="pl-4 text-green-600 dark:text-green-400">{"audit: READ,"}</p>
              <p>{"}"}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              La página <strong>Roles y permisos</strong> en{" "}
              <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/system/roles</code>{" "}
              muestra la matriz actual en tiempo real — útil para verificar el estado antes de redesplegar.
            </p>
          </section>

          {/* ─── usePermissions() ──────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Hook <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">usePermissions()</code></h2>
            <p className="text-sm text-muted-foreground">
              Úsalo en cualquier componente para condicionar visibilidad de botones, formularios o secciones.
            </p>
            <div className="bg-muted rounded-md p-4 text-xs font-mono space-y-1 overflow-x-auto">
              <p>{"const { can, role } = usePermissions()"}</p>
              <p className="mt-2 text-muted-foreground">{"// Mostrar botón de edición solo si tiene permiso"}</p>
              <p>{"{ can(\"write\", \"geo\") && <EditButton /> }"}</p>
              <p className="mt-2 text-muted-foreground">{"// Caso especial: acción disponible para dos condiciones"}</p>
              <p>{"const canUpload = can(\"write\", \"shipments\") || role === \"operator\""}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Si el usuario es superadmin,{" "}
              <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">can()</code>{" "}
              siempre retorna <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">true</code>{" "}
              sin consultar el mapa de permisos.
            </p>
          </section>

          {/* ─── Guards de ruta ────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Guards de ruta (<code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">RequireRole</code>)</h2>
            <p className="text-sm text-muted-foreground">
              Cada ruta protegida en <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">App.tsx</code>{" "}
              usa una constante derivada de <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">rolesWithPermission()</code>.
              Si el mapa cambia, el guard cambia solo — no hay arrays hardcodeados.
            </p>
            <div className="bg-muted rounded-md p-4 text-xs font-mono space-y-1 overflow-x-auto">
              <p className="text-muted-foreground">{"// Al inicio de App.tsx — se computan una sola vez"}</p>
              <p>{"const R_GEO   = rolesWithPermission(\"geo\",   \"visit\")  // [\"admin\", \"operator\"]"}</p>
              <p>{"const R_AUDIT = rolesWithPermission(\"audit\", \"visit\")  // [] si ambos tienen NONE"}</p>
              <p className="mt-2 text-muted-foreground">{"// En el JSX de rutas"}</p>
              <p>{"<RequireRole roles={R_AUDIT}><AuditPage /></RequireRole>"}</p>
              <p className="mt-2 text-muted-foreground">{"// Si roles=[] nadie puede entrar → redirige a /dashboard"}</p>
            </div>
          </section>

          {/* ─── Superadmin ────────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Superadmin vía variable de entorno</h2>
            <p className="text-sm text-muted-foreground">
              Un superadmin bypasea el mapa de permisos completamente — ve y puede hacer todo, sin importar
              qué diga <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">PERMISSIONS</code>.
              El rol en la DB no cambia; el privilegio se inyecta en el JWT al hacer login.
            </p>
            <div className="bg-muted rounded-md p-4 text-xs font-mono space-y-1">
              <p className="text-muted-foreground">{"# apps/api/.env"}</p>
              <p>{"SUPERADMIN_EMAILS=admin@empresa.com"}</p>
              <p className="mt-1 text-muted-foreground">{"# Múltiples emails separados por coma"}</p>
              <p>{"SUPERADMIN_EMAILS=admin@empresa.com,dev@empresa.com"}</p>
            </div>
            <div className="border rounded-lg p-4 space-y-2 text-sm">
              <p className="font-medium text-sm">Flujo técnico</p>
              <ol className="text-muted-foreground space-y-1 list-decimal list-inside text-xs">
                <li>Al hacer login, el API compara el email con <code className="font-mono bg-muted px-1 rounded">SUPERADMIN_EMAILS</code></li>
                <li>Si hay match, el JWT incluye <code className="font-mono bg-muted px-1 rounded">isSuperAdmin: true</code></li>
                <li><code className="font-mono bg-muted px-1 rounded">usePermissions()</code> detecta el flag y retorna <code className="font-mono bg-muted px-1 rounded">can = () =&gt; true</code></li>
                <li><code className="font-mono bg-muted px-1 rounded">RequireRole</code> deja pasar al usuario sin chequear el array de roles</li>
              </ol>
            </div>
            <p className="text-sm text-muted-foreground">
              Para revocar el acceso: remové el email de la variable y reiniciá el servidor. La sesión activa
              expirará en las próximas 8 horas (TTL del JWT).
            </p>
          </section>

          {/* ─── Referencia visual ─────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Referencia visual</h2>
            <p className="text-sm text-muted-foreground">
              La página{" "}
              <a href="/system/roles" className="underline underline-offset-4 text-foreground">
                Sistema → Roles y permisos
              </a>{" "}
              muestra la matriz completa en tiempo real. Filtra por rol y muestra qué acciones están habilitadas
              para cada sección del sidebar.
            </p>
          </section>

        </div>
      </div>
    </>
  )
}
